import React, { useMemo } from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';

import { Big } from '@shared/safe-big';
import { cn } from '@shared/utils/lib/ui';

import { observer } from 'mobx-react-lite';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions';

import { useMarginProfitLossChart } from './useMarginProfitLossChart';

type Props = {
  positionId?: string;
  className?: string;
};

const MarginProfitLossChart: React.FC<Props> = observer(({ positionId, className }) => {
  const { profitLoss } = useMarginPositionDetails({ positionId });
  const { totalProfitLossAbs } = useMarginProfitLossChart();

  const width = useMemo(() => {
    if (!profitLoss || profitLoss?.eq(0) || !totalProfitLossAbs || totalProfitLossAbs.eq(0)) return 0;

    const maxBarWidth = 50;
    const minBarWidth = 3;
    const positionProfitPercentage = profitLoss?.abs().div(totalProfitLossAbs);

    // Calculate the width of the positions profit but ensure it can never be more than 50% width to
    // ensure it doesn't overflow the bar
    return Math.min(maxBarWidth, Math.max(Big(maxBarWidth).times(positionProfitPercentage).toNumber(), minBarWidth));
  }, [profitLoss, totalProfitLossAbs]);

  return (
    <FlexLayout
      className={cn(
        'relative h-4 w-[140px] min-w-[140px] max-w-[140px] origin-center rounded-[8px] bg-color-background-neutral',
        className,
      )}
    >
      <div className='absolute bottom-1/2 left-1/2 z-10 h-14 w-2 translate-x-1/2 translate-y-1/2 bg-color-border-neutral' />
      {profitLoss?.gt(0) && (
        <div
          className='absolute bottom-1/2 left-[calc(50%+1px)] h-full translate-y-1/2 rounded-r-[8px] bg-color-background-success'
          style={{
            width: `${width}%`,
          }}
        />
      )}
      {profitLoss?.lt(0) && (
        <div
          className='absolute bottom-1/2 right-[calc(50%-1px)] h-full translate-y-1/2 rounded-l-[8px] bg-color-background-error'
          style={{
            width: `${width}%`,
          }}
        />
      )}
    </FlexLayout>
  );
});

export { MarginProfitLossChart };
